import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronRight,
  Crown,
  Flame,
  Globe2,
  MessageSquareOff,
  Mic2,
  Quote,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import AnimatedNumber from "@/components/AnimatedNumber";
import Reveal from "@/components/Reveal";
import { ASSETS } from "@/constants/assets";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/LanguageContext";

const FADE = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Landing() {
  const [leadOpen, setLeadOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();

  useEffect(() => {
    // Landing no longer auto-opens the lead modal — the new /register page is the entry point.
    // We keep the modal available if we want to re-enable a soft lead capture later.
  }, [user]);

  const startFlow = () => {
    if (user) return navigate("/dashboard");
    navigate("/register");
  };

  const onLeadSuccess = () => {
    setLeadOpen(false);
    navigate("/register");
  };

  return (
    <div className="min-h-screen bg-ph" data-testid="landing-page">
      <Header onStart={startFlow} />

      {/* HERO — Corporate navy panel + soft light content */}
      <section className="relative overflow-hidden bg-[#0B1726]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C9A45C] opacity-[0.06] blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#16263A] opacity-40 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <motion.div
              variants={FADE}
              initial="hidden"
              animate="visible"
              custom={0}
              className="h-eyebrow text-[#E8D2A1]"
            >
              Institut PROSPECT&rsquo;HEURE · {t("hero.kicker")}
            </motion.div>

            <motion.h1
              variants={FADE}
              initial="hidden"
              animate="visible"
              custom={1}
              className="h-hero mt-6 text-4xl md:text-5xl lg:text-6xl text-white"
            >
              <span className="block text-white">{t("hero.welcome")}</span>
              <span className="block text-white mt-2">{t("hero.title_1")}</span>
              <span className="block text-[#C9A45C] mt-2">{t("hero.title_2")}</span>
              <span className="block text-white mt-2">{t("hero.title_3")}</span>
            </motion.h1>

            <motion.p
              variants={FADE}
              initial="hidden"
              animate="visible"
              custom={2}
              className="mt-8 text-[17px] leading-[1.7] text-[#D0D5DD] max-w-xl"
            >
              {t("hero.subtitle")} {t("hero.description")}
            </motion.p>

            <motion.div
              variants={FADE}
              initial="hidden"
              animate="visible"
              custom={3}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <button
                onClick={startFlow}
                className="gold-btn-accent gold-btn-pill inline-flex items-center justify-center gap-2 px-7 py-3"
                data-testid="hero-start-btn"
              >
                {t("cta.start")} <ArrowRight size={16} />
              </button>
              <a
                href="#livre"
                className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full text-[14px] font-semibold text-white border border-white/25 hover:bg-white/10 transition-colors"
                data-testid="hero-discover-btn"
              >
                {t("hero.discover")}
              </a>
            </motion.div>

            <motion.div
              variants={FADE}
              initial="hidden"
              animate="visible"
              custom={4}
              className="mt-10 grid grid-cols-3 gap-6 max-w-lg"
            >
              {[
                { n: 25, s: "+", k: "hero.stat_years" },
                { n: 10, s: "K+", k: "hero.stat_people" },
                { n: 4.9, s: "/5", d: 1, k: "hero.stat_satisfaction" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-3xl md:text-4xl font-bold text-[#9A762F] tracking-tight">
                    <AnimatedNumber value={s.n} suffix={s.s} decimals={s.d || 0} />
                  </div>
                  <div className="text-[11px] text-[#98A2B3] tracking-wider uppercase mt-2">{t(s.k)}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            variants={FADE}
            initial="hidden"
            animate="visible"
            custom={2}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-sm overflow-hidden border border-[#332D21]">
              <img
                src={ASSETS.bookLake}
                alt="Prospecter sans se faire rejeter — guide"
                className="w-full h-[420px] md:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.22em] text-[#E8D2A1] uppercase font-semibold">
                    {t("book.edition")}
                  </div>
                  <div className="font-semibold text-white text-xl md:text-2xl leading-tight mt-1">
                    {t("book.subtitle")}
                  </div>
                </div>
                <div className="text-xs text-white/70 hidden sm:block">Roméo Chouaké</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM */}
      {/* PROBLEM cards eyebrow */}
      <section className="border-y border-[#E4E7EC] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-8">
          {[
            {
              icon: MessageSquareOff,
              title: "La peur de prospecter",
              text:
                "La majorité des personnes évitent la prospection par peur du rejet. Résultat : ventes plates, revenus instables.",
            },
            {
              icon: Flame,
              title: "Le rejet qui bloque la croissance",
              text:
                "Chaque « non » perçu comme un échec devient un mur invisible qui empêche d'aller plus loin.",
            },
            {
              icon: TrendingUp,
              title: "Le Processus Pro de PROSPECT'HEURE",
              text:
                "Une progression structurée pour transformer la prospection en une compétence naturelle et régulière.",
            },
          ].map((c, i) => (
            <motion.div
              key={i}
              variants={FADE}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              custom={i}
              className="ph-card p-8 rounded-sm"
            >
              <c.icon size={24} className="text-[#9A762F]" strokeWidth={1.75} />
              <h3 className="h-display text-xl md:text-2xl mt-4 text-[#0B1726]">{c.title}</h3>
              <p className="text-[15px] text-[#667085] leading-relaxed mt-3">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BOOK */}
      <BookSection />

      {/* CONFERENCES */}
      <ConferencesSection />

      {/* TRAINING */}
      <TrainingSection onStart={startFlow} />

      {/* COACH */}
      <CoachSection />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="ph-card-dark p-10 md:p-16 relative overflow-hidden rounded-2xl">
          <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#C9A45C] opacity-[0.12] blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="h-eyebrow text-[#E8D2A1]">
                Rejoignez la communauté
              </div>
              <h3 className="h-display text-3xl md:text-4xl lg:text-5xl text-white mt-3">
                Passez à l&rsquo;action <span className="text-[#C9A45C]">dès aujourd&rsquo;hui.</span>
              </h3>
              <p className="text-[#C4CBD7] mt-4 max-w-lg">
                Créez votre espace membre en 60 secondes. Le paiement se fait directement dans
                votre espace, en toute sécurité, une fois votre compte activé.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row md:justify-end gap-3">
              <button
                onClick={startFlow}
                className="gold-btn-accent gold-btn-pill inline-flex items-center justify-center gap-2 px-7 py-3"
                data-testid="cta-bottom-btn"
              >
                Créer mon compte <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <LeadCaptureModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        onSuccess={onLeadSuccess}
      />
    </div>
  );
}

function BookSection() {
  const { t } = useT();
  const benefits = useMemo(
    () => [0, 1, 2, 3, 4, 5].map((i) => t(`book.benefits.${i}`)),
    [t],
  );

  return (
    <section id="livre" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          variants={FADE}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <img
              src={ASSETS.bookVertical}
              alt="Prospecter sans se faire rejeter"
              className="w-full rounded-sm border border-[#332D21] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-white border border-[#E4E7EC] px-5 py-4 rounded-lg shadow-sm">
              <div className="text-[10px] tracking-[0.22em] text-[#9A762F] uppercase font-semibold">{t("book.format")}</div>
              <div className="text-[#0B1726] text-[14px] mt-1 font-semibold">{t("book.format_value")}</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={FADE}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-2 text-[#9A762F] text-xs tracking-[0.22em] uppercase font-semibold">
            <BookOpen size={14} strokeWidth={1.75} />
            {t("book.kicker")}
          </div>
          <h2 className="h-display text-3xl md:text-4xl lg:text-5xl text-[#0B1726] mt-4">
            {t("book.title_1")}
            <br />
            <span className="text-[#9A762F]">{t("book.title_2")}</span>
          </h2>
          <p className="text-[#667085] mt-5 leading-relaxed max-w-xl">
            {t("book.description")}
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {benefits.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-[15px] text-[#172033]"
                data-testid={`book-benefit-${i}`}
              >
                <span className="mt-0.5 text-[#9A762F]">
                  <Check size={16} />
                </span>
                {b}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

function ConferencesSection() {
  const media = [
    { src: ASSETS.confPointing, alt: "Audience de la conférence PROSPECT'HEURE brandissant le guide", tall: true },
    { src: ASSETS.confAudienceWide, alt: "Salle de conférence pleine — vue large" },
    { src: ASSETS.confHandshake, alt: "Remise du guide à un participant", tall: true },
    { src: ASSETS.confAudience2, alt: "Roméo devant l'audience captivée" },
    { src: ASSETS.confCrowdStanding, alt: "Public debout lors de la conférence" },
    { src: ASSETS.confRomeoStage, alt: "Roméo Chouaké sur scène avec logo Entraîneur d'Excellence", tall: true },
    { src: ASSETS.confBackAudience, alt: "Vue arrière de la salle pendant la présentation" },
  ];
  return (
    <section id="conferences" className="bg-[#F8F9FB] border-y border-[#E4E7EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-[#9A762F] text-xs tracking-[0.22em] uppercase font-semibold">
            <Mic2 size={14} strokeWidth={1.75} />
            Sur le terrain
          </div>
          <h2 className="h-display text-3xl md:text-4xl lg:text-5xl text-[#0B1726] mt-4">
            L&rsquo;énergie des conférences <span className="text-[#9A762F]">PROSPECT&rsquo;HEURE.</span>
          </h2>
          <p className="text-[#667085] mt-5 leading-relaxed">
            Des salles pleines, des transformations réelles. Chaque événement rassemble des
            participants venus d&rsquo;Afrique, d&rsquo;Europe et d&rsquo;Amérique du Nord.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4">
          {media.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.06 * i, duration: 0.6 }}
              className={`relative overflow-hidden border border-[#332D21] rounded-sm ${
                i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : "col-span-1 md:col-span-2"
              }`}
              data-testid={`conf-photo-${i}`}
            >
              <img
                src={m.src}
                alt={m.alt}
                loading="lazy"
                className={`w-full ${m.tall ? "h-72 md:h-full" : "h-48 md:h-64"} object-cover hover:scale-105 transition-transform duration-700 ease-out`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/70 via-transparent to-transparent pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrainingSection({ onStart }) {
  const items = [
    { icon: Target, label: "Modules progressifs conçus pour l'action" },
    { icon: Users, label: "Communauté internationale de praticiens" },
    { icon: TrendingUp, label: "Suivi de progression en temps réel" },
    { icon: Crown, label: "Accompagnement de l'Entraîneur d'Excellence" },
  ];
  return (
      <section id="entrainement" className="bg-white max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="max-w-2xl">
          <div className="text-xs text-[#9A762F] tracking-[0.22em] uppercase font-semibold">L&rsquo;entraînement</div>
        <h2 className="h-display text-3xl md:text-4xl lg:text-5xl text-[#0B1726] mt-4">
          Le guide ouvre la voie.
          <br />
          <span className="text-[#9A762F]">L&rsquo;entraînement transforme.</span>
        </h2>
        <p className="text-[#667085] mt-5 leading-relaxed">
          Une structure claire, des exercices pratiques, une évaluation continue et un
          accompagnement humain — pour passer immédiatement de la théorie aux résultats.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-4 gap-6">
        {items.map((it, i) => (
          <motion.div
            key={i}
            variants={FADE}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={i}
            className="ph-card rounded-sm p-6"
          >
            <it.icon size={22} className="text-[#9A762F]" strokeWidth={1.75} />
            <div className="text-[#172033] mt-3 text-[15px] font-medium leading-relaxed">{it.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10">
        <button
          onClick={onStart}
          className="gold-btn gold-btn-pill inline-flex items-center gap-2 px-6 py-3"
          data-testid="training-cta-btn"
        >
          Créer mon espace membre <ArrowRight size={16} />
        </button>
      </div>
    </section>
  );
}

function CoachSection() {
  return (
    <section id="entraineur" className="bg-white border-y border-[#E4E7EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          variants={FADE}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="lg:col-span-5 relative"
        >
          <img
            src={ASSETS.coachPortrait}
            alt="Roméo Chouaké"
            className="w-full rounded-sm border border-[#332D21] object-cover"
          />
        </motion.div>
        <motion.div
          variants={FADE}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
          className="lg:col-span-7"
        >
          <div className="text-xs text-[#9A762F] tracking-[0.22em] uppercase font-semibold">
            L&rsquo;entraîneur d&rsquo;excellence
          </div>
          <h2 className="h-display text-3xl md:text-4xl lg:text-5xl text-[#0B1726] mt-4">
            Roméo <span className="text-[#9A762F]">Chouaké</span>
          </h2>
          <p className="text-[#667085] mt-5 leading-relaxed max-w-xl">
            Originaire du Cameroun, résidant au Canada, Roméo Chouaké est un expert reconnu en
            prospection, vente, leadership et développement commercial. Plus de 25 ans
            d&rsquo;expérience et des milliers de personnes accompagnées à travers le monde.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {[
              { icon: Globe2, t: "Cameroun · Canada · International" },
              { icon: Users, t: "10 000+ personnes accompagnées" },
              { icon: TrendingUp, t: "25+ années de terrain" },
              { icon: Crown, t: "Entraîneur d'Excellence certifié" },
            ].map((v, i) => (
              <div
                key={i}
                className="flex items-start gap-3 text-[14px] text-[#172033] ph-card px-4 py-3"
              >
                <v.icon size={18} className="text-[#9A762F] shrink-0 mt-0.5" strokeWidth={1.75} />
                {v.t}
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-start gap-3 text-[#172033] max-w-xl">
            <Quote size={22} className="text-[#9A762F] shrink-0 mt-1" />
            <p className="italic text-lg font-medium leading-relaxed">
              « Le rejet n&rsquo;est pas votre ennemi. Il est le raccourci vers vos meilleurs
              clients — quand vous savez le lire. »
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
